import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { isPlainObject } from 'lodash-es';
import otpUiConfig, { INTL_IDS_KEY } from '#/otp-ui-config';

const INTL_PREFIX = 'planner';

/**
 * A hook that returns a memoized and internationalized version the otp-ui
 * config object. This should be used in lieu of the static settings file when
 * values in the config are used as user facing strings
 *
 * @returns {Object}
 */
const useIntlOtpUiConfig = () => {
  const intl = useIntl();

  /**
   * Recurse over the supplied object, through nested objects and arrays, to
   * find objects have an attribute with the key `INTL_IDS_KEY`. For those cases
   * take the entries in the object under that key and apply them to the parent:
   * using the key as is and passing the value to `react-intl` to fetch a string
   * in the user's selected language
   *
   * @type {Object} A cloned version of the supplied object containing localized
   * values for attributes identified in the `INTL_IDS_KEY` entries
   */
  const localizeConfig = useCallback(
    obj => {
      const localizedAttributes = {};

      Object.entries(obj).forEach(([k, v]) => {
        let localizedValue = v;

        if (k === INTL_IDS_KEY && isPlainObject(v)) {
          Object.entries(v).forEach(([configValue, intlId]) => {
            localizedAttributes[configValue] = intl.formatMessage({
              id: `${INTL_PREFIX}.${intlId}`,
            });
          });
        } else if (isPlainObject(v)) {
          localizedValue = localizeConfig(v);
        } else if (Array.isArray(v)) {
          localizedValue = v.map(arrayValue =>
            isPlainObject(arrayValue) ? localizeConfig(arrayValue) : arrayValue
          );
        }

        localizedAttributes[k] = localizedValue;
      });

      return {
        ...obj,
        ...localizedAttributes,
      };
    },
    [intl]
  );

  return useMemo(() => localizeConfig(otpUiConfig), [localizeConfig]);
};

export default useIntlOtpUiConfig;
